import React, { useMemo } from 'react';
import { Table, TableProps } from 'antd';
import { ContentGroup, ContentAttempt, ContentStatus } from '../types';
import { Link } from 'react-router-dom';

interface RecentActivityTableProps {
    filteredGroupedContents: ContentGroup[];
    selectedStatus: ContentStatus;
    onStatusSelect: (status: ContentStatus) => void;
    getContentStatus: (attempt: ContentAttempt) => ContentStatus;
}

const RecentActivityTable: React.FC<RecentActivityTableProps> = ({
                                                                     filteredGroupedContents,
                                                                     selectedStatus,
                                                                     onStatusSelect,
                                                                     getContentStatus
                                                                 }) => {

    const tableColumns: TableProps<ContentGroup>['columns'] = useMemo(() => {

        const getPrioritizedAttempt = (record: ContentGroup): ContentAttempt => {
            const successAttempts = record.allAttempts.filter(
                (a) => getContentStatus(a) === "Success"
            );

            if (successAttempts.length > 0) {
                return successAttempts.sort(
                    (a, b) => new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime()
                )[0];
            }

            return record.mostRecentAttempt;
        };

        return [
            {
                title: "Content",
                dataIndex: "content_title",
                sorter: (a, b) => a.content_title.localeCompare(b.content_title),
            },
            {
                title: "Level",
                dataIndex: "level_name",
                sorter: (a, b) => a.level_name.localeCompare(b.level_name),
            },
            {
                title: "Skill",
                dataIndex: "category_name",
                sorter: (a, b) => a.category_name.localeCompare(b.category_name),
            },
            {
                title: "Last Opened",
                render: (_, record: ContentGroup) => {
                    const attempt = getPrioritizedAttempt(record);
                    return attempt.opened_at
                        ? new Date(attempt.opened_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                        })
                        : "";
                },
                sorter: (a, b) => {
                    const attemptA = getPrioritizedAttempt(a);
                    const attemptB = getPrioritizedAttempt(b);
                    return new Date(attemptA.opened_at).getTime() - new Date(attemptB.opened_at).getTime();
                },
            },
            {
                title: "Last Status",
                render: (_, record: ContentGroup) => {
                    const attempt = getPrioritizedAttempt(record);
                    let badgeClass = "";
                    const label: ContentStatus = getContentStatus(attempt);

                    if (label === "Opened") {
                        badgeClass = "bg-purple";
                    } else if (label === "Success") {
                        badgeClass = "bg-success";
                    } else {
                        badgeClass = "bg-warning";
                    }

                    return (
                        <span
                            className={`badge badge-sm ${badgeClass} d-inline-flex align-items-center`}
                        >
                        <i className="fa-solid fa-circle fs-5 me-1" />
                            {label}
                    </span>
                    );
                },
                sorter: (a, b) => {
                    const getStatusRank = (attempt: ContentAttempt) => {
                        const status = getContentStatus(attempt);
                        if (status === "Opened") return 0;
                        if (status === "In Progress") return 1;
                        return 2;
                    };

                    const attemptA = getPrioritizedAttempt(a);
                    const attemptB = getPrioritizedAttempt(b);

                    return getStatusRank(attemptA) - getStatusRank(attemptB);
                },
            },
        ];
    }, [getContentStatus]);

    const subTableColumns: TableProps<ContentAttempt>['columns'] = useMemo(() => [
        {
            title: "Attempt Date",
            dataIndex: "opened_at",
            render: (date: string) =>
                date
                    ? new Date(date).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                    : "",
            sorter: (a, b) => new Date(a.opened_at).getTime() - new Date(b.opened_at).getTime(),
        },
        {
            title: "Status",
            dataIndex: "is_solved",
            render: (_: any, record: ContentAttempt) => {
                let badgeClass = "";
                const label: ContentStatus = getContentStatus(record);

                if (label === "Opened") {
                    badgeClass = "bg-purple";
                } else if (label === "Success") {
                    badgeClass = "bg-success";
                } else {
                    badgeClass = "bg-warning";
                }
                return (
                    <span className={`badge badge-sm ${badgeClass} d-inline-flex align-items-center`}>
                        <i className="fa-solid fa-circle fs-5 me-1" />
                        {label}
                    </span>
                );
            },
        },
        {
            title: "Score",
            dataIndex: "score",
            render: (_: any, record: ContentAttempt) => {
                if (record.max_score && record.is_solved) {
                    const score = record.score ? parseFloat(record.score) : 0;
                    const maxScore = record.max_score ? parseFloat(record.max_score) : 0;
                    return `${score} / ${maxScore}`;
                }
                return "N/A";
            },
        }
    ], [getContentStatus]);

    const expandedRowRender = (record: ContentGroup) => (
        <div style={{ padding: '8px 0' }}>
            <Table
                dataSource={record.allAttempts}
                columns={subTableColumns}
                pagination={false}
                size="small"
            />
        </div>
    );

    const CustomExpandIcon = ({ expanded, onExpand, record }: any) => (
        <span
            onClick={e => onExpand(record, e)}
            style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: '#000',
                padding: '0 5px',
                transition: 'transform 0.3s ease-in-out',
                display: 'inline-block',
                transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
                lineHeight: 1
            }}
            className="fa fa-plus"
            aria-label={expanded ? "Collapse row" : "Expand row"}
        />
    );

    return (
        <div className="tickets">
            <div className="d-flex align-items-center justify-content-between flex-wrap page-title">
                <h5>Progress</h5>
            </div>

            <div className="col-12 mb-3 d-flex justify-content-end">
                <div className="dropdown">
                    <Link
                        to="#"
                        className="dropdown-toggle btn rounded border d-inline-flex align-items-center bg-danger text-white"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        Status
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                        {["All", "Opened", "In Progress", "Success"].map((status) => (
                            <li key={status}>
                                <Link
                                    to="#"
                                    className={`dropdown-item rounded-1 ${selectedStatus === status ? 'active bg-light fw-bold text-dark' : ''}`}
                                    onClick={() => onStatusSelect(status as ContentStatus)}
                                >
                                    {status}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="table-responsive">
                <Table
                    dataSource={filteredGroupedContents}
                    columns={tableColumns}
                    expandable={{
                        expandedRowRender,
                        expandIcon: CustomExpandIcon,
                        expandIconColumnIndex: 0,
                    }}
                />
            </div>
        </div>
    );
};

export default React.memo(RecentActivityTable);